import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Send, Loader2, CheckCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      toast.error('Please fill in every field before sending.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { name, email, subject, message });
      setSent(true);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="contact-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6 transition-colors" data-testid="contact-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </Link>

      <Card className="rounded-2xl shadow-md border overflow-hidden">
        <CardHeader className="pb-4">
          <div className="h-12 w-12 rounded-2xl bg-primary/15 flex items-center justify-center mb-3">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="font-serif text-2xl tracking-tight" data-testid="contact-title">Contact Us</CardTitle>
          <CardDescription>Questions, feedback, or partnership enquiries — send us a message and we'll get back to you.</CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="py-6 text-center" data-testid="contact-success">
              <CheckCircle className="h-14 w-14 text-secondary mx-auto mb-3" />
              <h3 className="font-serif text-xl font-bold">Message sent!</h3>
              <p className="text-sm text-muted-foreground mt-2 mb-6">Thanks for reaching out — we'll reply to <strong>{email}</strong> as soon as we can.</p>
              <Button variant="outline" className="rounded-full" onClick={() => { setSent(false); setSubject(''); setMessage(''); }} data-testid="contact-send-another">
                Send another message
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="contact-name">Your name</Label>
                  <Input id="contact-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" required data-testid="contact-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-email">Your email</Label>
                  <Input id="contact-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="contact-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contact-subject">Subject</Label>
                <Input id="contact-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What's this about?" required maxLength={200} data-testid="contact-subject-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contact-message">Message</Label>
                <Textarea id="contact-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us what's on your mind..." rows={7} required maxLength={5000} data-testid="contact-message-input" />
                <p className="text-xs text-muted-foreground text-right">{message.length} / 5000</p>
              </div>
              <Button type="submit" size="lg" disabled={submitting} className="w-full rounded-full transition-transform active:scale-95" data-testid="contact-submit-btn">
                {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</>) : (<><Send className="h-4 w-4 mr-2" /> Send Message</>)}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
